import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { Dispatch, SetStateAction } from 'react';
import { ifProp } from 'styled-tools';
import { useTranslations } from '~/services/umbraco/hooks';
import { Button } from '~/shared/components/Button';
import { LayoutRadioButton } from '~/shared/components/Form/components';
import { Modal } from '~/shared/components/Modal';
import { Skeleton } from '~/shared/components/SkeletonLoader/styled';
import { Text } from '~/shared/components/Text';
import useActiveCustomer from '../../../useActiveCustomer';

type Props = {
    setShowOverlay: Dispatch<SetStateAction<boolean>>;
    showOverlay: boolean;
};

export default function UserAccountModal({ setShowOverlay, showOverlay }: Props) {
    const { translate } = useTranslations();
    const {
        customers,
        isLoading,
        activeCustomerId,
        setActiveCustomerId,
        storedCustomerId,
        updateStoredCustomerId,
    } = useActiveCustomer();

    function onSubmit() {
        updateStoredCustomerId(activeCustomerId);
        setShowOverlay(false);
    }

    function onCancel() {
        if (activeCustomerId !== storedCustomerId) {
            setActiveCustomerId(storedCustomerId!);
        }
        setShowOverlay(false);
    }

    return (
        <Modal handleClose={onCancel} isOpen={showOverlay}>
            <Container
                initial={{ x: '100%', opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: '100%', opacity: 0 }}
                transition={{ duration: 0.2 }}
                key="changeAccount"
            >
                <Text variant="display2">{translate('mit-fog.userProfile.changeAccount')}</Text>
                <Text>{translate('mit-fog.userProfile.accountInformation.subtitle')}</Text>
                <Accounts>
                    {isLoading
                        ? Array.from({ length: 3 }).map((_, index) => (
                              <li key={index}>
                                  <AccountItem isActive={false}>
                                      <LayoutRadioButton
                                          checked={false}
                                          aria-labelledby="loading"
                                          name="loading"
                                      />
                                      <span>
                                          <div>
                                              <Skeleton height={21} width={230} />
                                              <Skeleton height={21} width={120} />
                                          </div>
                                          <Skeleton height={18} width={100} />
                                      </span>
                                  </AccountItem>
                              </li>
                          ))
                        : customers?.customers?.map((customer) => (
                              <li key={customer.customerId}>
                                  <AccountItem
                                      isActive={customer.customerId === activeCustomerId}
                                      onClick={() => setActiveCustomerId(customer.customerId!)}
                                  >
                                      <LayoutRadioButton
                                          checked={customer.customerId === activeCustomerId}
                                          aria-labelledby={customer.customerId}
                                          onChange={() => undefined}
                                          name={customer.customerName!}
                                      />
                                      <span>
                                          <div>
                                              <Text
                                                  id={customer.customerId}
                                                  as="label"
                                                  aria-label={customer.customerName}
                                                  variant="display6"
                                              >
                                                  {customer.customerName}
                                              </Text>
                                              <Text variant="uiLinks">{customer.customerId}</Text>
                                          </div>
                                          {customer.customerId === storedCustomerId ? (
                                              <Text variant="uiLinks">
                                                  {translate(
                                                      'mit-fog.userProfile.accountInformation.loggedIn',
                                                  )}
                                              </Text>
                                          ) : null}
                                      </span>
                                  </AccountItem>
                              </li>
                          ))}
                </Accounts>
                <Button
                    disabled={activeCustomerId === storedCustomerId}
                    fullWidth
                    noIcon={activeCustomerId === storedCustomerId}
                    onClick={onSubmit}
                >
                    {translate('mit-fog.userProfile.changeAccount')}
                </Button>
            </Container>
        </Modal>
    );
}

const Container = styled(motion.div)(({ theme }) => ({
    display: 'grid',
    paddingBottom: theme.space[4],
    gap: theme.space[4],
    maxHeight: '100%',
    height: '100%',
    gridTemplateRows: `${theme.space[5]} ${theme.space[6]} auto ${theme.space[6]}`,
}));

const Accounts = styled.ul(({ theme }) => ({
    display: 'flex',
    gap: theme.space[2],
    flexDirection: 'column',
    overflowY: 'scroll',
    '::-webkit-scrollbar': {
        display: 'none',
    },
}));

const AccountItem = styled.button<{ isActive: boolean }>(
    ({ theme }) => ({
        cursor: 'pointer',
        padding: theme.space[2],
        display: 'flex',
        width: '100%',
        border: `1px solid ${theme.colors.grey50}`,
        borderRadius: 2,
        height: theme.space[8],
        transition: 'background-color 200ms ease, border 200ms ease, box-shadow 200ms ease',
        backgroundColor: 'transparent',

        '&:hover, &:active, &:focus': {
            borderColor: theme.colors.brandBlue,
        },

        span: {
            display: 'flex',
            flex: 1,
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: theme.space[2],

            div: {
                display: 'flex',
                minHeight: theme.space[6],
                flexDirection: 'column',
                alignItems: 'flex-start',
            },
        },
    }),
    ifProp('isActive', ({ theme }) => ({
        borderColor: theme.colors.brandBlue,
        backgroundColor: theme.colors.grey30,
    })),
);
